import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertCircle, Printer, Users, Trash2, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AddPairingForm } from '@/components/rounds/AddPairingForm';
import { PairingsRoundCard } from '@/components/rounds/PairingsRoundCard';
import { GenerateRoundButtons } from '@/components/rounds/GenerateRoundButtons';
import { useRoundsController } from './rounds/useRoundsController';
import { useHubOutletContext } from './tournament-hub/TournamentHubLayoutRoute';

export function RoundsPage() {
  const { t } = useTranslation('director');
  const { id: tournamentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Story 083 — God Mode is hub-wide; the header owns the toggle.
  const { godMode, role } = useHubOutletContext();
  const isOwner = role === 'owner';
  const ctrl = useRoundsController(tournamentId, godMode, isOwner);
  const {
    tournament,
    rounds,
    loading,
    selectedRound,
    setSelectedRound,
    players,
    showAddPairing,
    setShowAddPairing,
    editMode,
    setEditMode,
    setSwapSelection,
    showCompletionPrompt,
    setShowCompletionPrompt,
    showDeletePrompt,
    setShowDeletePrompt,
    showFallbackNotice,
    setShowFallbackNotice,
    handleCompleteTournament,
    handleUncompleteTournament,
    handleDeleteRound,
    handleAddManualPairing,
    currentRound,
    canGenerate,
    hasIncompleteRounds,
    activePlayerCount,
    allPlannedRoundsDone,
    canCompleteTournament,
    gameConfig,
  } = ctrl;
  const koFinishTop = tournament?.koFinishTop ?? 1;

  if (loading) return <div className="p-8">{t('rounds_page.loading')}</div>;

  return (
    <div data-testid="rounds-page" className="w-full">
      <header className="border-b bg-card px-6 py-3">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center gap-4">
          {tournament && (
            <>
              <Badge variant="secondary" data-testid="tournament-status">
                {(() => {
                  const key = `rounds_page.tournament_status.${tournament.status}`;
                  const translated = t(key);
                  return translated !== key
                    ? translated
                    : tournament.status
                        .replace(/_/g, ' ')
                        .replace(/^\w/, (c) => c.toUpperCase());
                })()}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/tournaments/${tournamentId}/print`)}
              >
                <Printer className="mr-1 h-4 w-4" />
                {t('rounds_page.reports_button')}
              </Button>
            </>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        {/* Round tabs */}
        {rounds.length > 0 && (
          <div className="mb-4 flex items-center gap-2 overflow-x-auto" data-testid="round-tabs">
            <span className="text-sm font-medium text-muted-foreground shrink-0">{t('rounds_page.round_tab_label')}</span>
            {rounds.map((round, index) => (
              <Button
                key={round.id}
                variant={selectedRound === index ? 'default' : 'outline'}
                size="sm"
                className="h-8 min-w-8 px-2"
                data-testid={`round-tab-${round.roundNumber}`}
                onClick={() => { setSelectedRound(index); setShowFallbackNotice(false); }}
              >
                {round.roundNumber}
              </Button>
            ))}
          </div>
        )}

        {/* Generate button — Story 093: pairing generation is owner-only. */}
        {isOwner && <GenerateRoundButtons ctrl={ctrl} />}

        {/* Complete / Uncomplete tournament */}
        {canCompleteTournament && (
          <div className="mb-6">
            <Button
              data-testid="complete-tournament-button"
              onClick={() => setShowCompletionPrompt(true)}
            >
              <Trophy className="mr-1 h-4 w-4" />
              {t('rounds_page.buttons.complete_tournament')}
            </Button>
          </div>
        )}
        {tournament?.status === 'completed' && godMode && (
          <div className="mb-6">
            <Button
              variant="outline"
              data-testid="uncomplete-tournament-button"
              onClick={handleUncompleteTournament}
            >
              {t('rounds_page.buttons.uncomplete_tournament')}
            </Button>
          </div>
        )}

        {/* Tournament completion dialog */}
        <Dialog open={showCompletionPrompt} onOpenChange={setShowCompletionPrompt}>
          <DialogContent data-testid="complete-tournament-dialog">
            <DialogHeader>
              <DialogTitle>{t('rounds_page.complete_dialog.title')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {activePlayerCount <= 1
                ? t('rounds_page.complete_dialog.one_player_left')
                : koFinishTop > 1 && activePlayerCount <= koFinishTop
                  ? t('rounds_page.complete_dialog.top_x_reached', {
                      n: activePlayerCount,
                    })
                  : allPlannedRoundsDone
                    ? t('rounds_page.complete_dialog.all_rounds_done')
                    : ''}
              {t('rounds_page.complete_dialog.body')}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompletionPrompt(false)}>
                {t('rounds_page.complete_dialog.cancel_button')}
              </Button>
              <Button data-testid="confirm-complete-tournament" onClick={handleCompleteTournament}>
                <Trophy className="mr-1 h-4 w-4" />
                {t('rounds_page.complete_dialog.confirm_button')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete round dialog */}
        <Dialog open={showDeletePrompt} onOpenChange={setShowDeletePrompt}>
          <DialogContent data-testid="delete-round-dialog">
            <DialogHeader>
              <DialogTitle>
                {t('rounds_page.delete_round_dialog.title', {
                  roundNumber: currentRound?.roundNumber ?? '',
                })}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              {t('rounds_page.delete_round_dialog.body')}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeletePrompt(false)}>
                {t('rounds_page.delete_round_dialog.cancel_button')}
              </Button>
              <Button
                variant="destructive"
                data-testid="confirm-delete-round"
                onClick={() => {
                  setShowDeletePrompt(false);
                  handleDeleteRound();
                }}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                {t('rounds_page.delete_round_dialog.confirm_button')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Fallback pairing notice — transient, shown only after generation */}
        {showFallbackNotice && (
          <Card className="mb-4 border-blue-200 bg-blue-50" data-testid="fallback-pairing-notice">
            <CardContent className="flex items-center gap-2 py-3">
              <AlertCircle className="h-4 w-4 shrink-0 text-blue-500" />
              <p className="text-sm text-blue-800">
                {t('rounds_page.fallback_notice')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Pairings table */}
        <PairingsRoundCard ctrl={ctrl} />

        {/* Add pairing UI for paired rounds (edit mode only) */}
        {editMode && currentRound && currentRound.status === 'paired' && (
          <div className="mt-4">
            {!showAddPairing ? (
              <Button
                variant="outline"
                size="sm"
                data-testid="add-pairing-button"
                onClick={() => setShowAddPairing(true)}
              >
                <Users className="mr-1 h-4 w-4" />
                {t('rounds_page.add_pairing_button')}
              </Button>
            ) : (
              <AddPairingForm
                players={players}
                currentPairings={currentRound.pairings}
                gameConfig={gameConfig}
                onAdd={handleAddManualPairing}
                onCancel={() => setShowAddPairing(false)}
              />
            )}
          </div>
        )}

        {/* Empty state */}
        {rounds.length === 0 && !canGenerate && !hasIncompleteRounds && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <p>{t('rounds_page.empty_state')}</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
